import type { ReactNode } from 'react'
import { AlertTriangle, Construction, Inbox, RotateCw, WifiOff, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { errorMessage, referenceCode } from '@/lib/notify'
import { cn } from '@/lib/utils'

interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  /** Guía breve: qué significa el vacío y qué puede hacer el usuario. */
  description?: ReactNode
  /** Acción principal sugerida (p. ej. botón "Registrar cliente"). */
  action?: ReactNode
  className?: string | undefined
  /** `compact` para usar dentro de tarjetas pequeñas. */
  size?: 'default' | 'compact'
}

/** Estado vacío con ícono, explicación y acción sugerida. */
export function EmptyState({ icon: Icon = Inbox, title, description, action, className, size = 'default' }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'default' ? 'px-6 py-14' : 'px-4 py-8',
        className,
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <Icon className="size-6" aria-hidden />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}

interface ErrorStateProps {
  error: unknown
  /** Reintenta la consulta (p. ej. `refetch`). */
  onRetry?: (() => void) | undefined
  title?: string | undefined
  className?: string | undefined
}

/** Estado de error de una consulta, con mensaje del API, código de referencia y reintento. */
export function ErrorState({ error, onRetry, title, className }: ErrorStateProps) {
  const offline = error instanceof ApiError && error.isNetworkError
  const code = referenceCode(error)
  return (
    <EmptyState
      className={className}
      icon={offline ? WifiOff : AlertTriangle}
      title={title ?? (offline ? 'Sin conexión' : 'No se pudo cargar la información')}
      description={
        <>
          {errorMessage(error)}
          {code && <span className="mt-1 block text-xs">Código de referencia: <span className="tabular">{code}</span></span>}
        </>
      }
      action={
        onRetry && (
          <Button variant="outline" onClick={onRetry}>
            <RotateCw /> Reintentar
          </Button>
        )
      }
    />
  )
}

/** Marcador de pantallas pendientes de construir (las reemplazarán otros módulos). */
export function UnderConstruction({ description }: { description?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card">
      <EmptyState
        icon={Construction}
        title="En construcción"
        description={description ?? 'Esta sección estará disponible pronto.'}
      />
    </div>
  )
}
